"use client";

import csrfCookie from "@/util/csrfCookie";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { renderToString } from 'react-dom/server';

/**
 * @typedef {React.InputHTMLAttributes<HTMLInputElement> & {
 *   label: String | import("react").ReactNode;
 *   ariaLabel?: String;
 *   autoformatNumericOnly?: boolean;
 *   autoformatDecimalOnly?: boolean;
 *   autoformatAlphaNumericOnly?: boolean;
 *   autoformatDate?: boolean;
 *   equalMaxLengthValidation?: boolean;
 *   customClass?: string;
 *   disableRequiredAsterisk?: boolean;
 *   onValidate?: Function;
 *   placeholderErrorMessage?: string | import("react").ReactNode;
 *   errorMessage?: string | import("react").ReactNode;
 * }} FormInputTextProps
 */

/**
 * Text Form Input
 * @param {FormInputTextProps} props Props
 * @param {String} props.name Use no spaces - use dashes instead
 * @param {String} [props.type] email, tel, text
 * @param {String | undefined} [props.placeholder] Input placeholder
 * @param {String | undefined} [props.placeholderErrorMessage] Input placeholder
 * @param {import("react").ReactNode} [props.errorMessage] Validation error message
 * @param {Number} [props.maxLength] Input max-length
 * @param {String | import("react").ReactNode} props.label Input label
 * @param {String | undefined} [props.ariaLabel] Input aria-label
 * @param {import("react").MouseEventHandler<HTMLInputElement> | undefined} [props.onClick] onCLick function handler
 * @param {Function | undefined} [props.onValidate] Runs method on validation
 * @param {Boolean} [props.autoformatAlphaNumericOnly] Force alphanumeric only
 * @param {Boolean} [props.autoformatNumericOnly] Force numbers only
 * @param {Boolean} [props.autoformatDecimalOnly] Force decimal numbers only
 * @param {Boolean} [props.autoformatDate] Force mm/dd/yyyy date format
 * @param {Boolean} [props.equalMaxLengthValidation] Force validation to match string length
 * @param {Boolean} [props.required] Required flag
 * @param {String} [props.customClass] Add custom class names
 * @param {Boolean} [props.disabled] Disable element
 * @param {Boolean} [props.disableRequiredAsterisk] Disable the required asterisk
 * @param {React.Ref<{ validate(): boolean; testValidation(): boolean; readonly name: string; readonly valid: boolean; readonly value: any; reset(): void; }>} forwardRef - ref
 * @returns {import("react").JSX.Element} Text input Element
 */
const FormInputText = forwardRef(function FormInputText(/** @type {FormInputTextProps} */ props, ref) {
  const {
    name,
    type = 'text',
    placeholder = undefined,
    placeholderErrorMessage = undefined,
    errorMessage = <>This field is required.</>,
    maxLength = 100,
    label,
    ariaLabel = undefined,
    required = false,
    onClick = undefined,
    onBlur = undefined,
    onValidate = undefined,
    autoformatAlphaNumericOnly = false,
    autoformatNumericOnly = false,
    autoformatDecimalOnly = false,
    autoformatDate = false,
    equalMaxLengthValidation = false,
    customClass = undefined,
    disabled = false,
    disableRequiredAsterisk = false,
    ...rest
  } = props;

  const inputRef = useRef(null);
  const [valid, setValid] = useState(true);
  
  const autoformat = useMemo(() => {
    let autoformatTemp = undefined;

    if (type === 'tel') {
      autoformatTemp = 'phonenumber';
    } else if (type === 'zip') {
      autoformatTemp = 'zipcode';
    }

    return autoformatTemp;
  }, [type]);

  const regexFilterPattern = useMemo(() => {
    let finalPattern = undefined;

    switch (type) {
      case 'text':
        if (autoformatAlphaNumericOnly) {
          finalPattern = /[^a-zA-Z0-9\s\-\.‘']/g;        } else if (autoformatDate) {
          finalPattern = /[^\d/]/g;        } else if (autoformatDecimalOnly) {
          finalPattern = /[^\d.]/g;
        } else if (!autoformatNumericOnly) {
          finalPattern = /[^A-Za-z\s\-\.‘']/g;
        } else {
          finalPattern = /[^\d]/g;
        }
        break;
      case 'email':
        finalPattern = /[^a-zA-Z0-9_\-\.@'']/g;
        break;
      case 'tel':
        finalPattern = /[^0-9-()\s]*$/;
        break;
      case 'zip':
        finalPattern = /[^0-9-]*$/;
        break;
      default:
        break;
    }

    return finalPattern;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, autoformatNumericOnly, autoformatDecimalOnly, autoformatDate]);

  const regexValidationPattern = useMemo(() => {
    let finalPattern = undefined;

    switch (type) {
      case 'text':
        if (autoformatAlphaNumericOnly) {
          finalPattern = /^[A-Za-z0-9. ''-]+$/;        } else if (autoformatDate) {
          finalPattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;        } else if (autoformatDecimalOnly) {
          finalPattern = /^\d*\.?\d*$/;
        } else if (!autoformatNumericOnly) {
          // eslint-disable-next-line no-useless-escape
          finalPattern = /^[A-Za-z. ’'-]+$/;
        } else {
          finalPattern = /^\d+$/;
        }
        break;
      case 'email':
        // eslint-disable-next-line no-useless-escape
        finalPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        break;
      case 'tel':
        finalPattern = /^\(\d{3}\) \d{3}-\d{4}$/;
        // /^[0-9-()\s]*$/;
        break;
      case 'zip':
        finalPattern = /^\d{5}(-\d{4})?$/;
        break;
      default:
        break;
    }

    return finalPattern;
  }, [type, autoformatNumericOnly, autoformatDecimalOnly, autoformatAlphaNumericOnly, autoformatDate]);

  // Handle phonenumber/numeric autoformat

  const isFunction = (key) => {
    if (autoformat === 'numeric' || autoformat === 'zipcode' || autoformat === 'phonenumber') {
      return isAutoformatInput(key);
    } else {
      return true;
    }
  } 

  const isAutoformatInput = (key) => {
    return key !== undefined && key.match(regexFilterPattern) !== null;
  };

  const isModifierKey = (e) => {
    const key = e.keyCode;

    return (
      (e.shiftKey === true && isFunction(e.key)) ||
      key === 35 ||
      key === 36 || // Allow Shift, Home, End
      key === 8 ||
      key === 9 ||
      key === 13 ||
      key === 46 || // Allow Backspace, Tab, Enter, Delete
      (key > 36 && key < 41) || // Allow left, up, right, down
      ((e.ctrlKey === true || e.metaKey === true) &&
        (key === 65 || key === 67 || key === 86 || key === 88 || key === 90)) // Allow Ctrl/Command + A,C,V,X,Z
    );
  };

  const formatInput = (e) => {
    if (isModifierKey(e) && e.keyCode !== 8 && e.keyCode !== 46) {
      return;
    }

    var value = e.currentTarget.value;

    if (autoformat === 'phonenumber') {
      const input = value.replace(/\D/g, '').substring(0, 10);
      const areaCode = input.substring(0, 3);
      const middle = input.substring(3, 6);
      const last = input.substring(6, 10);

      if (input.length > 6) {
        value = `(${areaCode}) ${middle}-${last}`;
      } else if (input.length > 0 && input.length <= 3) {
        value = `(${areaCode}`;
      } else if (input.length >= 4) {
        value = `(${areaCode}) ${middle}`;
      }
    } else if (autoformat === 'zipcode') {
      const input = value.replace(/\D/g, '').substring(0, 10);
      const first = input.substring(0, 5);
      const last = input.substring(5, 10);

      if (input.length > 0 && input.length <= 5) {
        value = `${first}`;
      } else if (input.length >= 6) {
        value = `${first}-${last}`;
      }
    } else if (autoformatDate) {
      const digits = value.replace(/\D/g, '').substring(0, 8);
      const mm   = digits.substring(0, 2);
      const dd   = digits.substring(2, 4);
      const yyyy = digits.substring(4, 8);
      if (digits.length > 4) {
        value = `${mm}/${dd}/${yyyy}`;
      } else if (digits.length > 2) {
        value = `${mm}/${dd}`;
      } else if (digits.length > 0) {
        value = mm;
      } else {
        value = '';
      }
    }

    e.currentTarget.value = value;
  };

  // apiValidationData: { url: '/url-value', method: 'POST', bodyPropName: 'body-property-name', callback: (data) => { return <validation func> }}
  async function apiValidation(apiValidationData) {
    try {
      const key = inputRef.current?.value;
      const {url, method, callback, bodyPropName} = apiValidationData;
      const csrfToken = csrfCookie();
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': csrfToken, // CSRF token for security
        },
        body: JSON.stringify({
          [bodyPropName]: key
        }),
      });
      const data = await response.json();

      if (callback) {
        return callback(data);
      } else {
        return false;
      }
    } catch (error) {
      console.error('API validation error:', error);
      return false;
    }
  }

  function testValidation() {
    // required validation
    const key = inputRef.current?.value.trim();

    let isValid = (required && key !== '') || !required;

    if (isValid && key.length > 0 && equalMaxLengthValidation) {
      isValid = key.length === maxLength;
    }
    
    if (isValid && regexValidationPattern !== undefined && key !== undefined && key !== '') {
      isValid = regexValidationPattern.test(key);
    }

    return isValid;
  }

  function validate() {
    // required validation
    let isValid = testValidation();

    setValid(isValid);

    if (onValidate) {
      onValidate({name, valid: isValid});
    }

    return isValid;
  }

  const containerClass = useMemo(() => {
    var className = 'form-input-text';

    if (!valid) {
      className = `${className} form-input-text--invalid`;
    }

    if (customClass) {
      className = `${className} ${customClass}`;
    }

    return className;
  }, [valid, customClass]);

  useEffect(() => {
    // disable enter key
    const input = inputRef.current;
    function handleKeypress(e) {
      if (e.keyCode === 13) {
        e.preventDefault();
      }
    }
    input?.addEventListener('keypress', (e) => handleKeypress(e));

    return () => input.current?.removeEventListener('keypress', (e) => handleKeypress(e));
  }, []);

  /* Event handling */

  const handleKeydown = (e) => {
    if (!isFunction(e.key) && !isModifierKey(e)) {
      e.preventDefault();
    }
  };

  function handleKeyup(e) {
    formatInput(e);
  }

  function handleBlur(e) {
    validate();
    if (onBlur) onBlur(e);
  }

  function handleInput(e) {
    const input = e.currentTarget;
    input.value = input.value.replace(regexFilterPattern, '');
    if (autoformatDecimalOnly) {
      // Allow only one decimal point
      const parts = input.value.split('.');
      if (parts.length > 2) {
        input.value = parts[0] + '.' + parts.slice(1).join('');
      }
    }
  }

  useImperativeHandle(
    ref,
    () => ({
      validate,
      testValidation,
      apiValidation,
      get name() {
        return name;
      },
      get valid() {
        return valid;
      },
      setValid(val) {
        setValid(val);
      },
      get isValid() {
        return valid;
      },
      get value() {
        return inputRef.current?.value;
      },
      setValue(val) {
        if (inputRef.current) {
          inputRef.current.value = val;
        }
      },
      reset() {
        setValid(true);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      },
      get type() {
        return 'text';
      },
      setFocus() {
        if (window.innerWidth < 699) {
          /** @type {HTMLElement} */(document.activeElement)?.blur();
          const inputElement = inputRef.current;
          inputElement.blur();

          inputRef.current?.focus({ focusVisible: true });
          inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          inputRef.current?.focus({ focusVisible: true });
        }
      },
  }));

  const placeholderValue = useMemo(() => {
    let tempPlaceholder = placeholder;

    if (!valid && placeholderErrorMessage) {
      tempPlaceholder = typeof placeholderErrorMessage === 'string' ? placeholderErrorMessage : renderToString(placeholderErrorMessage).replace(/<\/?[^>]+(>|$)/g, "");
    }

    return tempPlaceholder;
  }, [placeholder, valid, placeholderErrorMessage]);

  return (
    <div className={containerClass}>
      <fieldset>
        {label !== null && label !== '' && <legend id={`form_input_${name}`}>
          {(!valid && required || required) && !disableRequiredAsterisk ? '*' : ''}{label}
        </legend>}
        <div className="form-input-text__wrapper">
          <input
            ref={inputRef}
            name={name}
            placeholder={placeholderValue}
            maxLength={maxLength}
            required={required}
            disabled={disabled}
            type={type}
            onBlur={handleBlur}
            aria-label={ariaLabel || renderToString(label)}
            onClick={onClick}
            onKeyUp={handleKeyup}
            onKeyDown={handleKeydown}
            onInput={handleInput}
            aria-invalid={!valid}
            aria-labelledby={`form_input_${name}`}
            aria-errormessage={!valid ? `form_input_${name}_error_message` : undefined}
            data-gtm-event-category="Form"
            data-gtm-event-action="Click"
            data-gtm-event-label={renderToString(label)}
            {...rest}
          />
        </div>
        {errorMessage && !valid && <div id={`form_input_${name}_error_message`} className="invalid-feedback text-sm mt-1">{errorMessage}</div>}
      </fieldset>
    </div>
  );
});

export default FormInputText;